import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import React, { memo } from 'react';
import { Controller } from 'react-hook-form';

/**
 * ControlledTextField is a wrapper around the Controller component from react-hook-form.
 * It is used to control the input fields in the form.
 * @param {object} props
 * @param {string} props.label - The label for the input field
 * @param {string} props.name - The name of the input field
 * @param {boolean} [props.required] - The required state of the input field
 * @param {boolean} [props.fullWidth] - The full width state of the input field
 * @param {boolean} [props.error] - The error state of the input field
 * @param {string | React.ReactNode | any} [props.helperText] - The helper text for the input field
 * @param {React.ReactNode} props.children - The children of the select field
 * @param { import('react-hook-form').Control<any, any>} props.control - The control object from react-hook-form
 * @param {Omit<import('react-hook-form').UseControllerProps, 'name'>} [props.controllerOptions] - The options for the controller
 * @param {import('@mui/material/Select').SelectProps} [props.selectProps] - The props for the select field
 * @param {import('@mui/material/InputLabel').InputLabelProps} [props.inputLabelProps] - The props for the input label
 */

const ControlledSelectComponent = ({
  name,
  control,
  label = '',
  required = false,
  fullWidth = false,
  controllerOptions = {},
  inputLabelProps = {},
  selectProps = {},
  children,
  error = false,
  helperText = '',
}) => {
  if (!name) {
    throw new Error('ControlledTextField: name is required');
  }
  if (!(controllerOptions?.control || control)) {
    throw new Error('ControlledTextField: control is required');
  }

  const id = `id_${name}`;

  return (
    <Controller
      control={control}
      name={name}
      {...controllerOptions}
      render={({ field, fieldState, formState }) => (
        <Stack spacing={1} width="100%">
          {label ? (
            <InputLabel
              htmlFor={id}
              id={`labelId_${id}`}
              required={required}
              shrink={false}
              {...inputLabelProps}
            >
              {label}
            </InputLabel>
          ) : null}
          <Select
            fullWidth={fullWidth}
            id={id}
            labelId={`labelId_${id}`}
            {...field}
            size="small"
            {...selectProps}
          >
            {children}
          </Select>
          {error ? <FormHelperText error>{helperText}</FormHelperText> : null}
        </Stack>
      )}
    />
  );
};

ControlledSelectComponent.displayName = 'ControlledSelect';

const ControlledSelect = memo(ControlledSelectComponent);

export default ControlledSelect;
